import { faClose } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { downloadPdfBlob, formatCurrency } from "@gymflow/helpers";
import { WalletBalanceHistoryDTO } from "@gymflow/types";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { usePageSize } from "apps/portal/src/hooks";
import { useClubSettings } from "apps/portal/src/providers";
import { apiV2, queryClient } from "libs/api/src/lib/apiV2";
import { DateTime } from "luxon";
import { useContext, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import { Button, DownloadIcon } from "../../atoms";
import { CreditCardDownIcon } from "../../atoms/icons/CreditCardDownIcon";
import { CreditCardUpIcon } from "../../atoms/icons/CreditCardUpIcon";
import { PaginatedTable } from "../../organisms";
import RangeDatePicker from "../../organisms/RangeDatePicker/RangeDatePicker";
import { PaymentsSidebarProviderContext } from "./components/PaymentsSidebarProviderContext";
import { formatDate } from "./helpers";

const UserMemberWalletHistory = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const {
    club: { timezone, defaultCurrency },
  } = useClubSettings();

  const todayLocal = DateTime.now().setZone(timezone);

  const defaultRange = useMemo(
    () => ({
      from: todayLocal.startOf("month").toJSDate(),
      to: todayLocal.startOf("day").toJSDate(),
    }),
    [todayLocal],
  );

  const [range, setRange] = useState(defaultRange);
  const { id: memberId } = useParams<{ id: string }>();
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const history = useHistory();
  const { t } = useTranslation();

  const pageSize = usePageSize({
    tableContainerRef,
    rowHeight: 56,
  });

  const dateFrom = range?.from
    ? DateTime.fromJSDate(range.from)
        .setZone(timezone, { keepLocalTime: true })
        .toISODate()
    : null;

  const dateTo = range?.to
    ? DateTime.fromJSDate(range.to)
        .setZone(timezone, { keepLocalTime: true })
        .toISODate()
    : null;

  const {
    data: walletHistoryData,
    isFetching,
    fetchNextPage,
    fetchPreviousPage,
  } = useInfiniteQuery(
    apiV2.staff.wallet.infiniteQueries.listHistory(memberId, {
      limit: pageSize,
      dateFrom,
      dateTo,
    }),
  );

  const currentPageData = walletHistoryData?.pages[currentPage]?.content ?? [];

  const { openUserStaffPaymentsSidebar } = useContext(
    PaymentsSidebarProviderContext,
  );

  const { data: member } = useQuery(apiV2.staff.member.queries.id(memberId));

  const columnHelper = createColumnHelper<WalletBalanceHistoryDTO>();

  const columns = [
    columnHelper.accessor("type", {
      header: "",
      enableSorting: false,
      cell: (column) => {
        const type = column.getValue();
        const isTopUp =
          type === "CREDIT_TOP_UP" ||
          type === "CREDIT_REDEEM_VOUCHER" ||
          type === "CREDIT_PURCHASE_REFUND";
        return (
          <div>
            {isTopUp ? (
              <div className="bg-success-50 flex size-8 items-center justify-center rounded-full">
                <CreditCardUpIcon pathClassName="stroke-success-600" />
              </div>
            ) : (
              <div className="bg-error-50 flex size-8 items-center justify-center rounded-full">
                <CreditCardDownIcon pathClassName="stroke-error-600" />
              </div>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor(
      (row) => {
        return {
          invoiceNumber: row.invoiceNumber,
          voucherCode: row.voucherCode,
        };
      },
      {
        cell: (info) => {
          const { invoiceNumber, voucherCode } = info.getValue();
          return invoiceNumber ? (
            <Button
              intent="transparent"
              className="min-w-24 p-1 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 active:outline-none active:ring-0 active:ring-offset-0"
              onClick={() => {
                if (memberId) {
                  openUserStaffPaymentsSidebar({ invoiceNumber, memberId });
                }
              }}
            >
              <div className="text-gray-600 hover:text-[#6186db]">
                #{invoiceNumber}
              </div>
            </Button>
          ) : (
            <div>{t("page.userWallets.voucher", { voucherCode })}</div>
          );
        },
        id: "id",
        header: () => t("page.userWallets.historyTable.id"),
        enableSorting: false,
      },
    ),
    columnHelper.accessor("amount", {
      header: () => t("page.userWallets.historyTable.amount"),
      enableSorting: false,
      cell: (column) => {
        const amount = column.getValue();
        return (
          <div className="text-sm font-bold text-gray-950">
            {formatCurrency(amount, defaultCurrency)}
          </div>
        );
      },
    }),
    columnHelper.accessor("createdAt", {
      header: () => t("page.userWallets.historyTable.date"),
      enableSorting: false,
      cell: (column) => {
        const date = column.getValue();
        return <div>{formatDate(date, timezone)}</div>;
      },
    }),
    columnHelper.accessor(
      (row) => {
        return {
          createdByFirstName: row.createdByFirstName,
          createdByLastName: row.createdByLastName,
        };
      },
      {
        cell: (info) => {
          const { createdByFirstName, createdByLastName } = info.getValue();
          const text =
            createdByFirstName || createdByLastName
              ? `${createdByFirstName ?? ""}  ${createdByLastName ?? ""}`
              : `${member?.firstName ?? ""}  ${member?.lastName ?? ""}`;
          return <div className="text-sm font-semibold">{text}</div>;
        },
        id: "actionBy",
        header: () => t("page.userWallets.historyTable.actionBy"),
        enableSorting: false,
      },
    ),
    columnHelper.accessor("invoiceNumber", {
      header: "",
      enableSorting: false,
      cell: (column) => {
        const invoiceNumber = column.getValue();
        return invoiceNumber ? (
          <Button
            onClick={async () => {
              const data = await queryClient.fetchQuery(
                apiV2.staff.member.queries.invoiceFile(memberId, invoiceNumber),
              );
              downloadPdfBlob(data, `Gymflow-${invoiceNumber}.pdf`);
            }}
          >
            <DownloadIcon pathClassName="stroke-gray-950" />
          </Button>
        ) : null;
      },
    }),
  ];

  return (
    <div className="bg-gray-0 flex h-screen flex-col justify-between">
      <div className="flex flex-col">
        <div className="relative flex flex-1 flex-row flex-wrap  justify-between pb-8 pl-8 pr-4 pt-10">
          <div className="flex w-full flex-col gap-6">
            <div className="flex flex-row justify-between">
              <div className="mr-4 flex max-w-[50rem] flex-col">
                <div className="text-lg font-semibold">
                  {t("page.userWallets.title")}
                </div>
                <div className="text-sm text-gray-600">
                  {t("page.userWallets.description")}
                </div>
              </div>
              <div
                className="mr-6 flex size-11 cursor-pointer justify-end"
                onClick={() => {
                  history.goBack();
                }}
              >
                <FontAwesomeIcon
                  className="text-xl text-gray-500"
                  icon={faClose}
                />
              </div>
            </div>
            <RangeDatePicker
              onDateSelectionApply={(newDate) => {
                if (newDate && newDate.from && newDate.to) {
                  setRange({ from: newDate.from, to: newDate.to });
                }
              }}
              dateRange={range}
            />
          </div>
        </div>
      </div>
      <PaginatedTable
        className="pb-10"
        tableProps={{
          data: currentPageData,
          columns: columns,
          isFetching,
          pageSize: pageSize,
          tableContainerRef,
          thClassName: "min-w-[5rem] text-xs",
        }}
        hasNextPage={
          !!walletHistoryData &&
          !!walletHistoryData.pages[currentPage]?.nextPageToken
        }
        hasPreviousPage={!!walletHistoryData && currentPage > 0}
        goToNextPage={() => {
          fetchNextPage();
          setCurrentPage(currentPage + 1);
        }}
        goToPreviousPage={() => {
          fetchPreviousPage();
          setCurrentPage(currentPage - 1);
        }}
      />
    </div>
  );
};

export default UserMemberWalletHistory;
